
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { saveAs } from 'file-saver';
import { pendingCitationsApi } from '@/api/pending-citations.api';
import { ToastProgrammatic as Toast, Upload } from 'buefy';
import RadialProgressBar from '@/jbi-shared/vue-components/RadialProgressBar.vue';
import Link from '@/components/base/Link.vue';
import { VueRef } from '@/utils/vue.types';
import { SwitchObject } from '@/jbi-shared/types/helpers';
import {
  CitationAuthor,
  IndividualCitationAuthor,
  OrganisationCitationAuthor
} from '@/jbi-shared/types/citation.fields';
import { CreateCitationDto } from '@/jbi-shared/types/citation.dto';

interface ImportSummary {
  total: number;
  success: number;
  failed: number;
}

interface FailedCitation {
  name: string;
  title: string;
  error: string;
}

interface ParsedCitation {
  type: string;
  title?: string;
  author?: CitationAuthor[];
  issued?: string;
  'container-title'?: string;
  'container-title-short'?: string;
  page?: string;
  volume?: string;
  issue?: string;
  DOI?: string;
  publisher?: string;
  url?: string;
  edition?: string;
  accessed?: string;
  publisherName?: string;
  publisherPlace?: string;
  editors?: string;
  eventName?: string;
  eventPlace?: string;
}

interface ParseResult {
  validCitations: ParsedCitation[];
  failedCitations: FailedCitation[];
}

@Component({
  components: {
    RadialProgressBar,
    Link
  }
})
export default class PendingCitationUploader extends Vue {
  public dropFiles: File | null = null;
  public isUploading = false;
  public isProcessing = false;
  public showResults = false;
  public resultType: 'success' | 'error' | 'partial' = 'success';
  public resultMessage = '';
  public importSummary: ImportSummary = { total: 0, success: 0, failed: 0 };
  public failedCitations: FailedCitation[] = [];
  public fileName = '';

  @Prop({ required: true }) public pendingDocumentId!: number;

  get uploadStatus() {
    if (this.isUploading) return 'Uploading file...';
    if (this.isProcessing) return 'Processing citations...';
    return '';
  }

  get shouldShowFooter() {
    // Show footer when there are partial results (some failures) or complete errors
    return (
      this.showResults &&
      (this.resultType === 'partial' || this.resultType === 'error')
    );
  }

  @Watch('shouldShowFooter')
  onFooterVisibilityChange(shouldShow: boolean) {
    this.$emit('footerVisibilityChange', shouldShow);
  }

  // browsers usually tend to open text files in a new tab
  // this is to force a file download
  private async downloadSampleFile(sampleType: string) {
    const ref = this.$refs.sampleDownloadLink as VueRef<HTMLAnchorElement>;
    const element = ref.$el;

    if (element.classList.contains('progress')) return;

    element.classList.add('progress');

    try {
      // Simpler if else statement
      if (sampleType === 'endnote') {
        saveAs(
          'https://storage.googleapis.com/cplus-static-documents/sample_endnote.xml',
          'endnote.xml'
        );
      } else {
        alert(`Unknown sample type: ${sampleType}`);
      }
    } catch (error) {
      alert('could not download sample file');
    }

    element.classList.remove('progress');
  }

  @Watch('dropFiles')
  async handleFileChange(file: File | null) {
    if (file) {
      this.fileName = file.name;
      await this.handleFileUpload(file);
    }
  }

  public async handleFileUpload(file: File) {
    const MAX_SIZE_MB = 10; // You can get this from ENV if needed
    const fileName = file.name;
    this.fileName = fileName;

    const toast = (message: string, type = 'is-danger') =>
      Toast.open({
        message: message,
        duration: 3000,
        type: type
      });

    const validationError = [
      {
        validate: () => /[~`!#$%\^&*+=\\[\]\\';,/{}|\\"<>\?]/g.test(fileName),
        message: `The file "${fileName}" contains special characters`
      },
      {
        validate: () => file.size > MAX_SIZE_MB * 1024 * 1024,
        message: `The file "${fileName}" is too large. (${MAX_SIZE_MB}MB max)`
      }
    ].find((validation) =>
      validation.validate() ? validation.message : false
    );

    if (validationError) {
      toast(validationError.message);
      this.clearFileUploader();
      return;
    }

    try {
      this.isUploading = true;
      this.showResults = false;

      const parseResult = await this.parseCitationFile(file);

      this.isUploading = false;
      this.isProcessing = true;

      let successCount = 0;
      let failedCount = 0;
      const failedCitations: FailedCitation[] = [];

      if (parseResult.validCitations.length > 0) {
        try {
          const citationDtos = parseResult.validCitations.map((citationData) =>
            this.convertToCreateCitationDto(citationData)
          );

          const importedCitations = await this.$store.dispatch(
            'pendingCitations/importPendingCitations',
            {
              documentId: this.pendingDocumentId,
              citationsData: citationDtos
            }
          );

          successCount = importedCitations.length;
        } catch (error) {
          failedCount = parseResult.validCitations.length;
          parseResult.validCitations.forEach((citationData, index) => {
            failedCitations.push({
              name: `Citation ${index + 1}`,
              title: citationData.title || '<untitled>',
              error: 'Failed to save to database'
            });
          });
        }
      }

      // Add validation failures to failed citations
      failedCitations.push(...parseResult.failedCitations);
      failedCount += parseResult.failedCitations.length;

      this.isProcessing = false;
      this.showResults = true;

      this.importSummary = {
        total:
          parseResult.validCitations.length +
          parseResult.failedCitations.length,
        success: successCount,
        failed: failedCount
      };

      this.failedCitations = failedCitations;

      if (successCount > 0 && failedCount === 0) {
        this.resultType = 'success';
        this.resultMessage = 'Citations imported successfully';
        toast(
          `Successfully imported citation(s) from ${fileName}`,
          'is-success'
        );

        // Only emit success and auto-close on complete success
        this.$emit('importSuccess', {
          total: this.importSummary.total,
          success: successCount,
          failed: failedCount
        });

        // Hide footer for complete success
        this.$emit('footerVisibilityChange', false);

        // Auto-close modal after a short delay to let user see the success message
        setTimeout(() => {
          this.resetState();
        }, 1500);
      } else if (successCount > 0 && failedCount > 0) {
        this.resultType = 'partial';
        this.resultMessage = 'Citations partially imported';

        // Emit success for the valid citations but don't auto-close
        this.$emit('importSuccess', {
          total: this.importSummary.total,
          success: successCount,
          failed: failedCount
        });

        this.$emit('footerVisibilityChange', true);

        // No auto-close, allow to see error
      } else if (successCount === 0 && failedCount > 0) {
        // Show detailed breakdown when all citations failed validation
        this.resultType = 'partial';
        this.resultMessage = 'Issues detected with citation file';
        this.$emit('importSuccess', {
          total: this.importSummary.total,
          success: successCount,
          failed: failedCount
        });

        // Emit footer visibility change to show the "Try again" button
        this.$emit('footerVisibilityChange', true);

        // Don't auto-close - let user review errors
      } else {
        this.resultType = 'error';
        this.resultMessage = 'No citations were successfully imported.';

        // Emit footer visibility change to show the "Try again" button
        this.$emit('footerVisibilityChange', true);

        // Don't auto-close - let user review errors
      }
    } catch (error) {
      this.isUploading = false;
      this.isProcessing = false;

      // Handle PubMed format error
      if (error && (error as Error).message === 'PUBMED_FORMAT_DETECTED') {
        toast('Unsupported file format. Please upload EndNote (.xml)');
        this.clearFileUploader();
        return;
      }

      // Handle XML syntax errors
      const err = error as Error;
      if (err && err.message && err.message.startsWith('XML_SYNTAX_ERROR:')) {
        const errorMessage = err.message.replace('XML_SYNTAX_ERROR:', '');
        this.showResults = true;
        this.resultType = 'partial';
        this.resultMessage = 'Issues detected with citation file';
        this.importSummary = { total: 1, success: 0, failed: 1 };
        this.failedCitations = [
          {
            name: 'Citation 1',
            title: '<untitled>',
            error: errorMessage
          }
        ];
        this.$emit('footerVisibilityChange', true);
        return;
      }

      this.showResults = true;
      this.resultType = 'error';
      this.resultMessage = 'Failed to process citation file. Please try again.';

      toast('Failed to import citations. Please try again.');
    }
  }

  private async parseCitationFile(file: File): Promise<ParseResult> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;

          // Check if this is a PubMed XML file
          if (
            content.includes('<PubmedArticleSet') ||
            content.includes('<PubmedArticle') ||
            content.includes('<PMID>') ||
            content.includes('<MedlineCitation') ||
            content.includes('<!DOCTYPE PubmedArticleSet')
          ) {
            reject(new Error('PUBMED_FORMAT_DETECTED'));
            return;
          }

          const parser = new DOMParser();
          const testDoc = parser.parseFromString(content, 'text/xml');
          const parserError = testDoc.querySelector('parsererror');

          if (parserError) {
            const recordMatches = content.match(/<record>[\s\S]*?<\/record>/g);

            if (recordMatches && recordMatches.length > 0) {
              // XML has parsing errors but contains processable records, use regex fallback
            } else {
              // Severe XML syntax error with no processable records
              const errorText = parserError.textContent || '';
              const errorMatch = errorText.match(
                /error on line (\d+) at column (\d+): (.+)/
              );

              if (errorMatch) {
                const [, line, column, message] = errorMatch;

                // Try to extract the actual problematic character from the XML content
                let problematicChar = '\\';
                try {
                  const lines = content.split('\n');
                  const errorLine = lines[parseInt(line) - 1];
                  if (errorLine && errorLine.length >= parseInt(column)) {
                    problematicChar = errorLine[parseInt(column) - 1] || '\\';
                  }
                } catch (e) {
                  // Fallback to backslash if we can't extract the character
                  problematicChar = '\\';
                }

                reject(
                  new Error(
                    `XML_SYNTAX_ERROR:Invalid attribute name Line: ${line} Column: ${column} Char: ${problematicChar}`
                  )
                );
                return;
              } else {
                reject(new Error('XML_SYNTAX_ERROR:Invalid XML format'));
                return;
              }
            }
          }

          const result = this.parseEndNoteXML(content);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  }

  private parseEndNoteXML(xmlContent: string): ParseResult {
    const validCitations: ParsedCitation[] = [];
    const failedCitations: FailedCitation[] = [];

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

      // Check for XML parsing errors
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        // Fallback: Use regex to extract individual record blocks from the XML string
        return this.parseWithRegexFallback(xmlContent);
      }

      // Try to find records in different possible structures
      let records = xmlDoc.getElementsByTagName('record');

      // If no records found, try regex fallback
      if (records.length === 0) {
        return this.parseWithRegexFallback(xmlContent);
      }

      for (let i = 0; i < records.length; i++) {
        try {
          const record = records[i];

          // Get citation type from ref-type element
          const refTypeElement = record.querySelector('ref-type');
          const refTypeName = refTypeElement?.getAttribute('name') || 'Generic';

          // Map EndNote reference types to CSL types
          const citationType = this.mapEndNoteTypeToCslType(refTypeName);

          const citation: ParsedCitation = {
            type: citationType
          };

          // Extract title
          const titleElement =
            record.querySelector('titles title style') ||
            record.querySelector('titles title') ||
            record.querySelector('title');
          if (titleElement) {
            citation.title = this.cleanString(titleElement.textContent);
          }

          // Extract authors
          const contributorsSection = record.querySelector('contributors');
          const authorElements = record.querySelectorAll(
            'contributors authors author'
          );
          if (authorElements.length > 0) {
            const authors: CitationAuthor[] = [];
            authorElements.forEach((authorElement) => {
              const styleElement = authorElement.querySelector('style');
              if (styleElement) {
                const authorText = this.cleanString(styleElement.textContent);
                if (authorText) {
                  const transformedAuthor =
                    this.transformEndNoteAuthor(authorText);
                  authors.push(transformedAuthor);
                }
              }
            });
            citation.author = authors;
          } else {
            citation.author = [];
          }

          // Extract publication year
          const yearElement =
            record.querySelector('dates year style') ||
            record.querySelector('dates year') ||
            record.querySelector('year');
          if (yearElement) {
            citation.issued = this.cleanString(yearElement.textContent);
          }

          // Extract journal name for journal articles
          const journalElement =
            record.querySelector('titles secondary-title style') ||
            record.querySelector('periodical full-title') ||
            record.querySelector('journal');
          if (journalElement) {
            citation['container-title'] = this.cleanString(
              journalElement.textContent
            );
          }

          // Extract journal abbreviation
          const journalAbbrElement = record.querySelector(
            'periodical abbr-2 style'
          );
          if (journalAbbrElement) {
            citation['container-title-short'] = this.cleanString(
              journalAbbrElement.textContent
            );
          }

          // Extract pages
          const pagesElement =
            record.querySelector('pages style') ||
            record.querySelector('pages');
          if (pagesElement) {
            citation.page = this.cleanString(pagesElement.textContent);
          }

          // Extract volume
          const volumeElement =
            record.querySelector('volume style') ||
            record.querySelector('volume');
          if (volumeElement) {
            citation.volume = this.cleanString(volumeElement.textContent);
          }

          // Extract issue
          const issueElement =
            record.querySelector('number style') ||
            record.querySelector('issue');
          if (issueElement) {
            citation.issue = this.cleanString(issueElement.textContent);
          }

          // Extract DOI
          const doiElement = record.querySelector(
            'electronic-resource-num style'
          );
          if (doiElement) {
            citation.DOI = this.cleanString(doiElement.textContent);
          }

          // Extract publisher
          const publisherElement =
            record.querySelector('publisher style') ||
            record.querySelector('publisher');
          if (publisherElement) {
            citation.publisher = this.cleanString(publisherElement.textContent);
          }

          // Extract URL
          const urlElement =
            record.querySelector('urls related-urls url style') ||
            record.querySelector('url');
          if (urlElement) {
            citation.url = this.cleanString(urlElement.textContent);
          }

          // Extract edition
          const editionElement =
            record.querySelector('edition style') ||
            record.querySelector('edition');
          if (editionElement) {
            citation.edition = this.cleanString(editionElement.textContent);
          }

          // Extract access date
          const accessDateElement =
            record.querySelector('access-date style') ||
            record.querySelector('access-date');
          if (accessDateElement) {
            citation.accessed = this.cleanString(accessDateElement.textContent);
          }

          // Type-specific field mapping
          if (citationType === 'paper-conference') {
            citation.publisherName = citation.publisher;
            citation.publisherPlace = '';
            citation.editors = '';
            citation.eventName = '';
            citation.eventPlace = '';
          }

          // Only add citation if it has required fields
          const validationError = this.validateCitation(citation, i + 1);

          if (validationError) {
            failedCitations.push({
              name: `Citation ${i + 1}`,
              title: citation.title || '<untitled>',
              error: validationError
            });
          } else {
            validCitations.push(citation);
          }
        } catch (error) {
          failedCitations.push({
            name: `Citation ${i + 1}`,
            title: '<Error parsing citation>',
            error: `Failed to parse citation: ${(error as Error).message}`
          });
        }
      }

      return {
        validCitations,
        failedCitations
      };
    } catch (error) {
      if (validCitations.length > 0 || failedCitations.length > 0) {
        return {
          validCitations,
          failedCitations
        };
      }

      throw new Error(
        `Failed to parse EndNote XML file: ${(error as Error).message}`
      );
    }
  }

  private parseWithRegexFallback(xmlContent: string): ParseResult {
    const validCitations: ParsedCitation[] = [];
    const failedCitations: FailedCitation[] = [];

    try {
      // Extract individual record blocks using regex
      const recordMatches = xmlContent.match(/<record>[\s\S]*?<\/record>/g);

      if (!recordMatches) {
        return { validCitations, failedCitations };
      }

      recordMatches.forEach((recordXml, index) => {
        try {
          // Parse each individual record
          const parser = new DOMParser();
          const recordDoc = parser.parseFromString(
            `<root>${recordXml}</root>`,
            'text/xml'
          );
          const record = recordDoc.querySelector('record');

          if (record) {
            const citation = this.extractCitationFromRecord(record, index);
            const validationError = this.validateCitation(citation, index + 1);

            if (validationError) {
              failedCitations.push({
                name: `Citation ${index + 1}`,
                title: citation.title || '<untitled>',
                error: validationError
              });
            } else {
              validCitations.push(citation);
            }
          }
        } catch (error) {
          failedCitations.push({
            name: `Citation ${index + 1}`,
            title: '<Error parsing citation>',
            error: `Failed to parse citation: ${(error as Error).message}`
          });
        }
      });

      return { validCitations, failedCitations };
    } catch (error) {
      throw new Error(
        `Failed to parse XML with fallback method: ${(error as Error).message}`
      );
    }
  }

  private extractCitationFromRecord(
    record: Element,
    index: number
  ): ParsedCitation {
    // Get citation type from ref-type element
    const refTypeElement = record.querySelector('ref-type');
    const refTypeName = refTypeElement?.getAttribute('name') || 'Generic';

    // Map EndNote reference types to CSL types
    const citationType = this.mapEndNoteTypeToCslType(refTypeName);

    const citation: ParsedCitation = {
      type: citationType
    };

    // Extract title
    const titleElement =
      record.querySelector('titles title style') ||
      record.querySelector('titles title') ||
      record.querySelector('title');
    if (titleElement) {
      citation.title = this.cleanString(titleElement.textContent);
    }

    const contributorsSection = record.querySelector('contributors');
    const authorElements = record.querySelectorAll(
      'contributors authors author'
    );

    if (authorElements.length > 0) {
      const authors: CitationAuthor[] = [];
      authorElements.forEach((authorElement) => {
        const styleElement = authorElement.querySelector('style');
        if (styleElement) {
          const authorText = this.cleanString(styleElement.textContent);
          if (authorText) {
            const transformedAuthor = this.transformEndNoteAuthor(authorText);
            authors.push(transformedAuthor);
          }
        }
      });
      citation.author = authors;
    } else {
      // Explicitly set empty array for citations without authors
      citation.author = [];
    }

    // Extract publication year
    const yearElement =
      record.querySelector('dates year style') ||
      record.querySelector('dates year') ||
      record.querySelector('year');
    if (yearElement) {
      citation.issued = this.cleanString(yearElement.textContent);
    }

    // Extract journal name for journal articles
    const journalElement =
      record.querySelector('titles secondary-title style') ||
      record.querySelector('periodical full-title') ||
      record.querySelector('journal');
    if (journalElement) {
      citation['container-title'] = this.cleanString(
        journalElement.textContent
      );
    }

    // Extract other fields... (keeping it simpler for the fallback)
    const pagesElement =
      record.querySelector('pages style') || record.querySelector('pages');
    if (pagesElement) {
      citation.page = this.cleanString(pagesElement.textContent);
    }

    const volumeElement =
      record.querySelector('volume style') || record.querySelector('volume');
    if (volumeElement) {
      citation.volume = this.cleanString(volumeElement.textContent);
    }

    return citation;
  }

  private validateCitation(
    citation: ParsedCitation,
    citationNumber: number
  ): string {
    if (!citation) return 'Invalid citation record';

    // General required fields validation
    const generalMissingFields = [];
    if (!citation.title || citation.title.trim() === '') {
      generalMissingFields.push('Title');
    }
    if (!citation.author || citation.author.length === 0) {
      generalMissingFields.push('Author');
    }
    if (!citation.issued || citation.issued.trim() === '') {
      generalMissingFields.push('Year/Published Date');
    }

    if (generalMissingFields.length > 0) {
      return `Missing required fields: ${generalMissingFields.join(', ')}`;
    }

    // Type-specific validation
    if (citation.type === 'article-journal') {
      const journalMissingFields = [];
      if (!citation.DOI || citation.DOI.trim() === '') {
        journalMissingFields.push('DOI');
      }
      if (
        !citation['container-title'] ||
        citation['container-title'].trim() === ''
      ) {
        journalMissingFields.push('Journal Name');
      }

      if (journalMissingFields.length > 0) {
        return `Missing required fields: ${journalMissingFields.join(', ')}`;
      }
    }

    if (citation.type === 'article') {
      const genericMissingFields = [];
      if (!citation.publisher || citation.publisher.trim() === '') {
        if (!citation.url || citation.url.trim() === '') {
          genericMissingFields.push('Publisher, URL');
        }
      }

      if (genericMissingFields.length > 0) {
        return `Missing required fields: ${genericMissingFields.join(', ')}`;
      }
    }

    return ''; // No validation errors
  }

  private cleanString(str: string | null | undefined): string {
    return str ? str.replace(/\s+/g, ' ').trim() : '';
  }

  private mapEndNoteTypeToCslType(endNoteType: string): string {
    const typeMapping: { [key: string]: string } = {
      Book: 'book',
      Generic: 'article',
      'Book Section': 'chapter',
      'Conference Paper': 'paper-conference',
      'Journal Article': 'article-journal',
      Report: 'report',
      WebPage: 'webpage'
    };

    return typeMapping[endNoteType] || 'article';
  }

  private transformEndNoteAuthor(authorString: string): CitationAuthor {
    // Handle different EndNote author formats
    if (authorString.includes(',')) {
      // Format: "LastName, FirstName"
      const parts = authorString.split(',').map((part) => part.trim());
      return {
        family: parts[0] || '',
        given: parts[1] || ''
      };
    } else {
      // Format: "FirstName LastName" or single name
      const parts = authorString.trim().split(' ');
      if (parts.length === 1) {
        return {
          given: parts[0],
          family: ''
        };
      } else {
        const lastName = parts[parts.length - 1];
        const firstName = parts.slice(0, -1).join(' ');
        return {
          given: firstName,
          family: lastName
        };
      }
    }
  }

  private convertToCreateCitationDto(
    citation: ParsedCitation
  ): CreateCitationDto {
    // Convert ParsedCitation to CreateCitationDto format
    // The API expects author as CitationAuthor[] array, not string
    return {
      type: citation.type,
      title: citation.title,
      issued: citation.issued,
      'container-title': citation['container-title'],
      'container-title-short': citation['container-title-short'],
      page: citation.page,
      volume: citation.volume,
      issue: citation.issue,
      DOI: citation.DOI,
      publisher: citation.publisher,
      url: citation.url,
      edition: citation.edition,
      accessed: citation.accessed,
      publisherName: citation.publisherName,
      publisherPlace: citation.publisherPlace,
      editors: citation.editors,
      eventName: citation.eventName,
      eventPlace: citation.eventPlace,
      // Send author array in the correct format expected by the backend
      ...(citation.author &&
        citation.author.length > 0 && {
          author: this.convertAuthorsToCSLFormat(citation.author)
        })
    };
  }

  private convertAuthorsToCSLFormat(
    authors: CitationAuthor[]
  ): CitationAuthor[] {
    if (!Array.isArray(authors)) {
      return [];
    }

    return authors.map((author): CitationAuthor => {
      // Check if it's an organization author (has literal)
      if ('literal' in author) {
        return {
          literal: author.literal
        } as OrganisationCitationAuthor;
      }
      // Otherwise treat as individual author (has given/family)
      else {
        const individualAuthor = author as IndividualCitationAuthor;
        return {
          given: individualAuthor.given || '',
          family: individualAuthor.family || ''
        } as IndividualCitationAuthor;
      }
    });
  }

  async resetState() {
    this.clearFileUploader();
    this.resetUploader();
  }

  public async handleRetryUpload() {
    // Public method that can be called from parent component's modal footer
    await this.resetState();
  }

  public resetUploader() {
    this.dropFiles = null;
    this.isUploading = false;
    this.isProcessing = false;
    this.showResults = false;
    this.resultType = 'success';
    this.resultMessage = '';
    this.importSummary = { total: 0, success: 0, failed: 0 };
    this.failedCitations = [];
    this.clearFileUploader();

    // Hide footer when resetting
    this.$emit('footerVisibilityChange', false);
  }

  private clearFileUploader() {
    const uploadRef = this.$refs.fileInput as Upload;
    const fileInput = uploadRef?.getElement();

    if (fileInput) fileInput.value = '';
    this.dropFiles = null;
  }

  public destroyed() {
    if (this.isUploading || this.isProcessing) {
      Toast.open({
        queue: true,
        message: `Citation import is running in the background`
      });
    }
  }
}
